import type {CheckoutDownloadProps, PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {Mode} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {
  LoadingSleepTime,
  ProductType,
  PublishAndDownloadMode,
  PublishingAnalyticsAction,
  PublishingPurchaseErrorStatus,
  SelectionType,
  Status,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import type {
  AJAXResponseForOrderError,
  AJAXResponseOrderGenerationStatus,
  AJAXResponseOrderSuccess,
  AJAXResponseOutOfFreeDownloads,
  AJAXResponsePurchaseProduct,
  OrderDetailsForRedux,
  VideoFreebieParams,
} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-panel.types';
import {GA4EventParamName} from '@Libraries/ga-events';
import {
  setFreebieProduct,
  setOrderDetails,
  setOrderError,
  setPremiumProductForPoster,
  setPremiumProductForPrint,
  setShowLoader,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';

import {SubFlowType} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-compiled-sub-panel/digital-signage-compiled-sub-panel.types';
import {isPremiumUser} from '@Utils/user.util';
import {getDefaultPosterProductDataForProcessDesign, showErrorOnPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import {getUserPremiumLevel, isUserPAYG} from '@Libraries/user.library';
import {noop} from '@Utils/general.util';

const CONVERT_TRIAL_MODE_OUT_OF_FREE_DOWNLOADS = 'out-of-free-downloads';
const MAX_ELAPSED_TIME_TO_SHOW_ERROR = 600;
const INCREMENT_STEP = 5;
const INCREMENT_INTERVAL = 500;
const DEFAULT_PERCENTAGE_INCREMENT = 10;
const SMALL_PERCENTAGE_INCREMENT = 5;
const UPDATE_GENERATION_PROGRESS_TIMEOUT = 2000;

const PROGRESS_PERCENTAGE_DEFAULT = 20;
const PROGRESS_PERCENTAGE_SMALL = 40;
const PROGRESS_PERCENTAGE_HALF = 50;
const PROGRESS_PERCENTAGE_FULL = 100;

let downloadElapsedTime = 0;
let ajaxCallMade = false;
let productUsed = getDefaultPosterProductDataForProcessDesign();

export function processDesignForPublishing(selectedProduct: PosterProductDataForProcessDesign): void {
  productUsed = selectedProduct;
  const {status} = productUsed;
  const productId = productUsed.id;
  const idPosterHash = window.PMW.redux.store.getState().publishingOptionsPanel.hashedId;
  const posterPageHash = window.PMW.redux.store.getState().publishingOptionsPanel.posterPageHashedId;
  const productProcessedPages = productUsed.existingPageOrders ?? {};
  const preservedStatuses = [Status.UNPURCHASED, Status.FREEBIE, Status.OUT_OF_FREE_DOWNLOADS, Status.NEEDS_ATTENTION];
  const isPreservedStatus = preservedStatuses.includes(status);
  const existingPageOrderId = productProcessedPages[posterPageHash];
  const existingPageOrderStatus = status === Status.NEW_ORDER ? Status.OUTDATED : status;

  const pageOrderId = existingPageOrderId ?? (isPreservedStatus ? productUsed.orderId : '0');
  const pageStatus = existingPageOrderId || isPreservedStatus ? existingPageOrderStatus : Status.NEW_ORDER;

  switch (pageStatus) {
    case Status.DOWNLOADABLE:
      processDesignForDownloadableOrder(pageOrderId);
      break;
    case Status.OUTDATED:
      processDesignForOutdatedOrder(pageOrderId);
      break;
    case Status.NEW_ORDER:
      processDesignForNewOrder(productId, idPosterHash, posterPageHash ? [posterPageHash] : []);
      break;
    case Status.OUT_OF_FREE_DOWNLOADS:
      processDesignForOutOfFreeDownloadsOrder(status, productId, posterPageHash ? [posterPageHash] : []);
      break;
    case Status.UNPURCHASED:
      processDesignForUnpurchasedOrder(productId, idPosterHash);
      break;
    case Status.GENERATING:
      processDesignForGeneratingOrder(pageOrderId);
      break;
    case Status.FREEBIE:
      processDesignForFreebieOrder(idPosterHash);
      break;
    case Status.NEEDS_ATTENTION:
      processDesignForNeedsAttentionStatus();
      break;
    default:
      break;
  }
}

export function processDesignPagesForEmbedPublishing(selectedProduct: PosterProductDataForProcessDesign, pageHashedIds?: string[]): void {
  productUsed = selectedProduct;
  const {status} = productUsed;
  const productId = productUsed.id;
  const idPosterHash = window.PMW.redux.store.getState().publishingOptionsPanel.hashedId;
  const posterPageHash = window.PMW.redux.store.getState().publishingOptionsPanel.posterPageHashedId;
  const productProcessedPages = productUsed.existingPageOrders ?? {};
  const preservedStatuses = [Status.UNPURCHASED, Status.FREEBIE, Status.OUT_OF_FREE_DOWNLOADS, Status.NEEDS_ATTENTION];
  const pageOrderId = productProcessedPages[posterPageHash] ?? (preservedStatuses.includes(status) ? productUsed.orderId : '0');
  const pageStatus = productProcessedPages[posterPageHash] || preservedStatuses.includes(status) ? status : Status.NEW_ORDER;
  const resolvedPageHashedIds = pageHashedIds ?? (posterPageHash ? [posterPageHash] : []);

  switch (pageStatus) {
    case Status.DOWNLOADABLE:
      processDesignForDownloadableOrder(pageOrderId);
      break;
    case Status.OUTDATED:
      processDesignForNewOrder(productId, idPosterHash, resolvedPageHashedIds);
      break;
    case Status.NEW_ORDER:
      processDesignForNewOrder(productId, idPosterHash, resolvedPageHashedIds);
      break;
    case Status.OUT_OF_FREE_DOWNLOADS:
      processDesignForOutOfFreeDownloadsOrder(status, productId, resolvedPageHashedIds);
      break;
    case Status.UNPURCHASED:
      processDesignForUnpurchasedOrder(productId, idPosterHash);
      break;
    case Status.GENERATING:
      processDesignForGeneratingOrder(pageOrderId);
      break;
    case Status.FREEBIE:
      processEmbedPagesFreebieDownload(idPosterHash, resolvedPageHashedIds);
      break;
    case Status.NEEDS_ATTENTION:
      processDesignForNeedsAttentionStatus();
      break;
    default:
      break;
  }
}

function processEmbedPagesFreebieDownload(idPosterHash: string, pageHashedIds: string[]): void {
  if (window.PMW.isReactPosterMaker()) {
    if (productUsed.isVideo) {
      executeOnPosterSave(() => {
        downloadEmbedPagesVideoFreebie(idPosterHash, pageHashedIds);
      });
    } else {
      executeOnPosterSave(() => {
        downloadEmbedPagesImageFreebie(idPosterHash, pageHashedIds);
      });
    }
  } else if (productUsed.isVideo) {
    downloadEmbedPagesVideoFreebie(idPosterHash, pageHashedIds);
  } else {
    downloadEmbedPagesImageFreebie(idPosterHash, pageHashedIds);
  }
  window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.DOWNLOAD, {[GA4EventParamName.TYPE]: 'free'});
}

function onPollingEmbedPagesFreebieImageDownloadSuccess(pageHashedIds: string[]): void {
  const updatedPagesData = {
    ...productUsed.existingPageOrders,
    ...Object.fromEntries(pageHashedIds.map((id) => [id, Status.FREEBIE])),
  };
  const updatedProductUsed = {
    ...productUsed,
    status: Status.DOWNLOADABLE,
    existingPageOrders: updatedPagesData,
  };
  const data = makeOrderDetailsUpdatedData(PROGRESS_PERCENTAGE_FULL, '', 0);
  window.PMW.redux.store.dispatch(setFreebieProduct(updatedProductUsed));
  window.PMW.redux.store.dispatch(setOrderDetails(data));
}

function pollingEmbedPagesImageFreebie(idPosterHash: string, pageHashedIds: string[]): void {
  void window.PMW.writeLocal('poster/getImageFreebieForEmbedPages', {
    hid: idPosterHash,
    pageIds: pageHashedIds,
  })
    .then(() => {
      onPollingEmbedPagesFreebieImageDownloadSuccess(pageHashedIds);
    })
    .catch(() => {
      showErrorOnPanel(window.i18next.t('pmwjs_something_wrong_reload_page'));
    });
}

const updateEmbedPagesDownloadProgressWithIntervals = (idPosterHash: string, pageHashedIds: string[]): void => {
  const progress = setInterval(() => {
    updateDownloadProgress(PROGRESS_PERCENTAGE_HALF);
    const {progressPercentage} = window.PMW.redux.store.getState().publishingOptionsPanel.orderDetails;

    if (progressPercentage >= 95) {
      window.PMW.redux.store.dispatch(setShowLoader(false));
      clearInterval(progress);
      ajaxCallMade = false;
      return;
    }

    if (!ajaxCallMade) {
      ajaxCallMade = true;
      pollingEmbedPagesImageFreebie(idPosterHash, pageHashedIds);
    }
  }, 100);
};

function downloadEmbedPagesImageFreebie(idPosterHash: string, pageHashedIds: string[]): void {
  updateDownloadProgress(PROGRESS_PERCENTAGE_DEFAULT);
  updateEmbedPagesDownloadProgressWithIntervals(idPosterHash, pageHashedIds);
}

function onPollingEmbedPagesFreebieVideoDownloadSuccess(response: VideoFreebieParams, progress: NodeJS.Timeout, pageHashedIds: string[]): void {
  if (response) {
    const updatedPagesData = {
      ...productUsed.existingPageOrders,
      ...Object.fromEntries(pageHashedIds.map((id) => [id, Status.FREEBIE])),
    };
    const updatedProductUsed = {
      ...productUsed,
      status: Status.DOWNLOADABLE,
      existingPageOrders: updatedPagesData,
    };
    window.PMW.redux.store.dispatch(setFreebieProduct(updatedProductUsed));
    const data = makeOrderDetailsUpdatedData(PROGRESS_PERCENTAGE_FULL, '', 0);
    clearInterval(progress);
    window.PMW.redux.store.dispatch(setOrderDetails(data));
    window.PMW.redux.store.dispatch(setShowLoader(false));
  }
}

function generateVideoFreebieForEmbedPagesRequest(posterHashId: string, pageHashedIds: string[]): Promise<void> {
  return window.PMW.writeLocal('poster/generateVideoFreebieForEmbedPages', {
    idPosterHash: posterHashId,
    pageIds: pageHashedIds,
  });
}

function pollingEmbedPagesVideoFreebie(idPosterHash: string, pageHashedIds: string[], progress: NodeJS.Timeout): void {
  void window.PMW.pollingAjaxCallAsync(generateVideoFreebieForEmbedPagesRequest.bind(null, idPosterHash, pageHashedIds), getVideoFreebieStatus.bind(null, idPosterHash))
    .then((response: VideoFreebieParams) => {
      onPollingEmbedPagesFreebieVideoDownloadSuccess(response, progress, pageHashedIds);
    })
    .catch(() => {
      showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
    });
}

function downloadEmbedPagesVideoFreebie(idPosterHash: string, pageHashedIds: string[]): void {
  updateDownloadProgress(DEFAULT_PERCENTAGE_INCREMENT);
  const progress = setInterval(() => {
    updateDownloadProgress(SMALL_PERCENTAGE_INCREMENT);
  }, UPDATE_GENERATION_PROGRESS_TIMEOUT);
  pollingEmbedPagesVideoFreebie(idPosterHash, pageHashedIds, progress);
}

function onCheckoutDownloadSuccessCallback(): void {
  // left blank intentionally for future implementation
}

function stripeCheckoutCloseCallback(): void {
  // left blank intentionally for future implementation
}

export function isNumber(value: string): boolean {
  const parsedValue = parseFloat(value);
  return !isNaN(parsedValue) && isFinite(parsedValue);
}

export function getMode(): string {
  const {subFlowType, selectionType} = window.PMW.redux.store.getState().publishingOptionsPanel;

  if (subFlowType !== SubFlowType.DEFAULT) {
    switch (subFlowType) {
      case SubFlowType.RAYDIANT_SUBDISPLAY:
        return Mode.RAYDIANT;
      case SubFlowType.SIGNAGELIVE_WIDGET_SUBDISPLAY:
        return Mode.SIGNAGELIVE;
      case SubFlowType.SCREEN_CLOUD_SUBDISPLAY:
        return Mode.SCREENCLOUD;
      default:
        return Mode.DOWNLOAD;
    }
  } else if (selectionType !== SelectionType.DEFAULT_DISPLAY) {
    switch (selectionType) {
      case SelectionType.DIGITAL_SIGNAGE_DISPLAY:
        return Mode.DIGITAL_SIGNAGE;
      case SelectionType.EMBED_WEBPAGE_DISPLAY:
        return Mode.EMBED_LINKS;
      default:
        return Mode.DOWNLOAD;
    }
  }
  return Mode.DOWNLOAD;
}

function setOrderDetailsInRedux(newPercentage: number, estimatedTimeStr: string, elapsedTime: number): void {
  window.PMW.redux.store.dispatch(
    setOrderDetails({
      progressPercentage: newPercentage,
      estimatedTimeInSeconds: estimatedTimeStr,
      timeElapsedInSeconds: elapsedTime,
      error: '',
    })
  );
}

/**
 * @param newPercentage
 * @param realPercentage
 * @param estimatedTimeStr
 */
const updateDownloadProgressIncrementally = (newPercentage: number, realPercentage: number, estimatedTimeStr: string): void => {
  let newUpdatedPercentage = newPercentage;
  const incrementIntervalId = setInterval(() => {
    if (newUpdatedPercentage < realPercentage) {
      newUpdatedPercentage += INCREMENT_STEP;
      if (newUpdatedPercentage > realPercentage) {
        newUpdatedPercentage = realPercentage;
      }
      setOrderDetailsInRedux(newUpdatedPercentage, estimatedTimeStr, downloadElapsedTime);
    } else {
      clearInterval(incrementIntervalId);
    }
  }, INCREMENT_INTERVAL);
};

/**
 * @param percentageDiff
 * @param isHeavyOrder
 */
function calculateSleepTime(percentageDiff: number, isHeavyOrder: boolean): number {
  let sleepTime: number;
  if (percentageDiff > PROGRESS_PERCENTAGE_DEFAULT && percentageDiff < PROGRESS_PERCENTAGE_SMALL) {
    sleepTime = LoadingSleepTime.SMALL;
  } else if (percentageDiff >= PROGRESS_PERCENTAGE_SMALL) {
    sleepTime = LoadingSleepTime.HIGH;
  } else {
    sleepTime = LoadingSleepTime.DEFAULT;
  }
  if (isHeavyOrder) {
    sleepTime *= 2;
  }
  return sleepTime;
}

const onUpdateIncompleteDownloadProgress = (newPercentage: number, realPercentage: number, estimatedTimeStr: string, previousPercentage: number): void => {
  if (realPercentage > previousPercentage) {
    updateDownloadProgressIncrementally(newPercentage, realPercentage, estimatedTimeStr);
  } else if (previousPercentage <= 90) {
    setOrderDetailsInRedux(previousPercentage + 5, estimatedTimeStr, downloadElapsedTime);
  }
};

export function updateDownloadProgress(realPercentage = 0, isHeavyOrder = false, estimatedTimeStr = ''): number {
  const previousPercentage = window.PMW.redux.store.getState().publishingOptionsPanel.orderDetails.progressPercentage;
  let newPercentage = previousPercentage;

  if (realPercentage === PROGRESS_PERCENTAGE_FULL) {
    newPercentage = realPercentage;
    setOrderDetailsInRedux(newPercentage, estimatedTimeStr, downloadElapsedTime);
    return 0;
  }
  if (realPercentage < PROGRESS_PERCENTAGE_FULL || newPercentage < PROGRESS_PERCENTAGE_FULL) {
    onUpdateIncompleteDownloadProgress(newPercentage, realPercentage, estimatedTimeStr, previousPercentage);
  }

  const percentageDiff = newPercentage - realPercentage;
  return calculateSleepTime(percentageDiff, isHeavyOrder);
}

export function updateOrderEmailNotification(orderId: string, value: boolean): void {
  void window.PMW.writeLocal('Ordergenerator/updateOrderNotificationForSendingEmail', {
    idorder: orderId,
    value: value ? 1 : 0,
  }).catch(noop);
}

function onGetStatusSuccessCompleted(data: OrderDetailsForRedux, idOrder: string, productType?: ProductType): void {
  data.progressPercentage = PROGRESS_PERCENTAGE_FULL;
  window.PMW.redux.store.dispatch(setOrderDetails(data));

  let currentProduct = productUsed;
  if (productType === ProductType.POSTER) {
    currentProduct = window.PMW.redux.store.getState().publishingOptionsPanel.premiumProductDataForPoster ?? productUsed;
  } else if (productType === ProductType.PRINT) {
    currentProduct = window.PMW.redux.store.getState().publishingOptionsPanel.premiumProductDataForPrint ?? productUsed;
  } else if (productType === ProductType.FREEBIE) {
    currentProduct = window.PMW.redux.store.getState().publishingOptionsPanel.freebieProductData ?? productUsed;
  }

  const updatedProductUsed: PosterProductDataForProcessDesign = {
    ...currentProduct,
    orderId: idOrder,
    status: Status.DOWNLOADABLE,
    needsPurchase: false,
  };

  if (productType === ProductType.POSTER) {
    window.PMW.redux.store.dispatch(setPremiumProductForPoster(updatedProductUsed));
  } else if (productType === ProductType.PRINT) {
    window.PMW.redux.store.dispatch(setPremiumProductForPrint(updatedProductUsed));
  } else if (productType === ProductType.FREEBIE) {
    window.PMW.redux.store.dispatch(setFreebieProduct(updatedProductUsed));
  }
  window.PMW.redux.store.dispatch(setShowLoader(false));
}

function onGetStatusSuccessInProgress(response: AJAXResponseOrderGenerationStatus, idOrder: string): void {
  const {percentageDone, estimatedTimeStr, heaviness} = response;
  const showLoader = window.PMW.redux.store.getState().publishingOptionsPanel.showLoader;
  const isHeavyOrder: boolean = parseInt(heaviness, 10) > 0;
  const sleepTime = updateDownloadProgress(percentageDone, isHeavyOrder, estimatedTimeStr);
  downloadElapsedTime += sleepTime;

  if (downloadElapsedTime > MAX_ELAPSED_TIME_TO_SHOW_ERROR) {
    updateOrderEmailNotification(idOrder, true);
    const text = `ERROR: Unable to generate the order:${idOrder} in ${downloadElapsedTime}seconds.`;
    window.PMW.redux.store.dispatch(setOrderError(text));
  } else if (showLoader) {
    if (Number(percentageDone) < PROGRESS_PERCENTAGE_FULL) {
      getOrderGenerationStatus(idOrder, percentageDone, sleepTime);
    } else {
      setTimeout(() => {
        onGetStatusSuccess(idOrder, response);
      }, 3000);
    }
  }
}

function makeOrderDetailsUpdatedData(percentageDone: number, estimatedTimeStr: string, downloadElapsedTime: number): OrderDetailsForRedux {
  return {
    progressPercentage: percentageDone,
    estimatedTimeInSeconds: estimatedTimeStr,
    timeElapsedInSeconds: downloadElapsedTime,
    error: '',
  };
}

export function onGetStatusSuccess(idOrder: string, response: AJAXResponseOrderGenerationStatus): void {
  if (response) {
    const {percentageDone, estimatedTimeStr, error} = response;
    const {productType} = window.PMW.redux.store.getState().publishingOptionsPanel;
    const {progressPercentage} = window.PMW.redux.store.getState().publishingOptionsPanel.orderDetails;

    if (error === Status.NEEDS_ATTENTION) {
      showErrorOnPanel(window.i18next.t('pmwjs_order_generation_error'));
      updateOrderEmailNotification(idOrder, true);
      return;
    }

    const pageId = window.PMW.redux.store.getState().publishingOptionsPanel.posterPageHashedId;
    const updatedPagesData = pageId
      ? {
          ...(productUsed.existingPageOrders ?? {}),
          [pageId]: idOrder,
        }
      : (productUsed.existingPageOrders ?? {});

    const data = makeOrderDetailsUpdatedData(percentageDone, estimatedTimeStr, downloadElapsedTime);
    const updatedOrderId = {
      ...productUsed,
      orderId: idOrder,
      existingPageOrders: updatedPagesData,
    };

    if (productType === ProductType.POSTER) {
      window.PMW.redux.store.dispatch(setPremiumProductForPoster(updatedOrderId));
    } else if (productType === ProductType.PRINT) {
      window.PMW.redux.store.dispatch(setPremiumProductForPrint(updatedOrderId));
    } else if (productType === ProductType.FREEBIE) {
      window.PMW.redux.store.dispatch(setFreebieProduct(updatedOrderId));
    }

    productUsed = updatedOrderId;

    if (percentageDone < PROGRESS_PERCENTAGE_FULL || progressPercentage < PROGRESS_PERCENTAGE_FULL) {
      onGetStatusSuccessInProgress(response, idOrder);
    } else {
      onGetStatusSuccessCompleted(data, idOrder, productType);
    }
  } else {
    showErrorOnPanel(window.i18next.t('pmwjs_stock_cannot_be_downloaded'));
  }
}

export function getOrderGenerationStatus(idOrder: string, percentageDone: number, sleepTime: number): void {
  void window.PMW.readLocal('cart/getRealTimeOrderProgress', {id: idOrder, percentageDone, sleepTime})
    .then((response: AJAXResponseOrderGenerationStatus) => {
      onGetStatusSuccess(idOrder, response);
    })
    .catch(() => {
      showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
    });
}

export function showDownloadProgress(data: AJAXResponseOrderSuccess): void {
  const {idOrder} = data;
  if (idOrder && ((isNumber(idOrder) && parseInt(idOrder, 10) > 0) || !isNumber(idOrder))) {
    getOrderGenerationStatus(idOrder, 0, LoadingSleepTime.DEFAULT);
  } else {
    showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
  }
}

export function onGenerateOrderSuccess(response: AJAXResponseOrderSuccess): void {
  try {
    if (response) {
      window.PMW.gtm.trackGA4PremiumDownloadEvents(response);
      const isRealTime = response.isRealTime ? 1 : 0;
      if (isRealTime) {
        showDownloadProgress(response);
      } else {
        window.location.href = window.PMW.util.site_url(`cart/preparingDownload/${response.type}/${response.idOrder}/${isRealTime}`);
      }
    }
  } catch (e) {
    showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
  }
}

const onNewPremiumDownloadError = (e: AJAXResponseForOrderError): void => {
  switch (e.code) {
    case PublishingPurchaseErrorStatus.PERMISSION_DENIED:
      showErrorOnPanel(window.i18next.t('pmwjs_download_designs_you_own'));
      return;
    case PublishingPurchaseErrorStatus.GETTY_REQUIREMENTS_NOT_MET:
      showErrorOnPanel(window.i18next.t('pmwjs_cannot_buy_only_getty_images'));
      return;
    case PublishingPurchaseErrorStatus.ORDER_NOT_FOUND:
    case PublishingPurchaseErrorStatus.PURCHASE_NOT_FOUND:
    case PublishingPurchaseErrorStatus.PARAM_MISSING:
    case PublishingPurchaseErrorStatus.QUEUE_ERROR:
    default:
      showErrorOnPanel(window.i18next.t('pmwjs_something_wrong_reload_page'));
  }
};

export function newPremiumDownload(productId: number, posterId: string, pageHashedIds?: string[]): void {
  const downloadOptions = pageHashedIds?.length ? {pagesHashedIds: pageHashedIds} : null;
  void window.PMW.writeLocal('cart/premiumDownload/', {
    productId: productId,
    hid: posterId,
    is: true,
    downloadOptions: downloadOptions,
    clientSource: 'publishing-process-design-library:newPremiumDownload',
    clientTrace: new Error().stack ?? '',
  })
    .then((response: AJAXResponseOrderSuccess) => {
      onGenerateOrderSuccess(response);
    })
    .catch((e: AJAXResponseForOrderError) => {
      onNewPremiumDownloadError(e);
    });
}

function newPurchaselessPaygDownload(productId: number, posterId: string, pageHashedIds?: string[]): void {
  const downloadOptions = pageHashedIds?.length ? {pagesHashedIds: pageHashedIds} : null;

  void window.PMW.writeLocal('cart/purchaselessPAYGDownload', {
    productId: productId,
    hid: posterId,
    is: true,
    downloadOptions: downloadOptions,
  })
    .then((response: AJAXResponseOrderSuccess) => {
      onGenerateOrderSuccess(response);
    })
    .catch((e: unknown) => {
      onNewPremiumDownloadError(e as AJAXResponseForOrderError);
    });
}

export function onFreeDownloadFromReactBuilder(idPosterHash: string): void {
  if (productUsed.isVideo) {
    executeOnPosterSave(() => {
      downloadVideoFreebie(idPosterHash);
    });
  } else {
    executeOnPosterSave(() => {
      downloadImagefreebie(idPosterHash);
    });
  }
}

function onPollingFreebieImageDownloadSuccess(): void {
  const pageId = window.PMW.redux.store.getState().publishingOptionsPanel.posterPageHashedId;
  const updatedPagesData = {
    ...productUsed.existingPageOrders,
    [pageId]: Status.FREEBIE,
  };

  const updatedProductUsed = {
    ...productUsed,
    status: Status.DOWNLOADABLE,
    existingPageOrders: updatedPagesData,
  };

  const data = makeOrderDetailsUpdatedData(PROGRESS_PERCENTAGE_FULL, '', 0);
  window.PMW.redux.store.dispatch(setFreebieProduct(updatedProductUsed));
  window.PMW.redux.store.dispatch(setOrderDetails(data));
}

function pollingFreebieImageDownload(idPosterHash: string): void {
  const pageId = window.PMW.redux.store.getState().publishingOptionsPanel.posterPageHashedId;
  void window.PMW.writeLocal('poster/getImageFreebie', {
    hid: idPosterHash,
    phid: pageId,
  })
    .then(() => {
      onPollingFreebieImageDownloadSuccess();
    })
    .catch(() => {
      showErrorOnPanel(window.i18next.t('pmwjs_something_wrong_reload_page'));
    });
}

const updateDownloadProgressWithIntervals = (idPosterHash: string): void => {
  const progress = setInterval(() => {
    updateDownloadProgress(PROGRESS_PERCENTAGE_HALF);
    const {progressPercentage} = window.PMW.redux.store.getState().publishingOptionsPanel.orderDetails;

    if (progressPercentage >= 95) {
      window.PMW.redux.store.dispatch(setShowLoader(false));
      clearInterval(progress);
      ajaxCallMade = false;
      return;
    }

    if (!ajaxCallMade) {
      ajaxCallMade = true;
      pollingFreebieImageDownload(idPosterHash);
    }
  }, 100);
};

function downloadImagefreebie(idPosterHash: string): void {
  // Call updateDownloadProgress every 1 seconds to show fake image generation progress
  updateDownloadProgress(PROGRESS_PERCENTAGE_DEFAULT);
  updateDownloadProgressWithIntervals(idPosterHash);
}

function onPollingFreebieVideoDownloadSuccess(response: VideoFreebieParams, progress: NodeJS.Timeout): void {
  if (response) {
    const pageId = window.PMW.redux.store.getState().publishingOptionsPanel.posterPageHashedId;
    const updatedPagesData = {
      ...productUsed.existingPageOrders,
      [pageId]: Status.FREEBIE,
    };

    const updatedProductUsed = {
      ...productUsed,
      status: Status.DOWNLOADABLE,
      existingPageOrders: updatedPagesData,
    };

    window.PMW.redux.store.dispatch(setFreebieProduct(updatedProductUsed));
    const data = makeOrderDetailsUpdatedData(PROGRESS_PERCENTAGE_FULL, '', 0);

    clearInterval(progress);
    window.PMW.redux.store.dispatch(setOrderDetails(data));
    window.PMW.redux.store.dispatch(setShowLoader(false));
  }
}

function pollingFreebieVideoDownload(posterHashId: string, progress: NodeJS.Timeout): void {
  void window.PMW.pollingAjaxCallAsync(generateVideoFreebie.bind(null, posterHashId), getVideoFreebieStatus.bind(null, posterHashId))
    .then((response: VideoFreebieParams) => {
      onPollingFreebieVideoDownloadSuccess(response, progress);
    })
    .catch(() => {
      showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
    });
}

function downloadVideoFreebie(idPosterHash: string): void {
  updateDownloadProgress(DEFAULT_PERCENTAGE_INCREMENT);
  const progress = setInterval(() => {
    updateDownloadProgress(SMALL_PERCENTAGE_INCREMENT);
  }, UPDATE_GENERATION_PROGRESS_TIMEOUT);

  pollingFreebieVideoDownload(idPosterHash, progress);
}

function generateVideoFreebie(posterHashId: string): Promise<void> {
  const pageId = window.PMW.redux.store.getState().publishingOptionsPanel.posterPageHashedId;
  return window.PMW.readLocal('poster/generateVideoFreebieNew', {
    idPosterHash: posterHashId,
    phid: pageId,
  });
}

function getVideoFreebieStatus(posterHashId: string): Promise<void> {
  return window.PMW.readLocal('poster/getVideoFreebieStatus', {
    posterHashId,
  });
}

export function purchaseDownload(productId: number, hashedId: string): void {
  const mode = getMode();
  void window.PMW.writeLocal('cart/checkoutPublish', {
    idposter: hashedId,
    idproduct: productId,
    mode,
    phids: JSON.stringify([window.PMW.redux.store.getState().publishingOptionsPanel.posterPageHashedId]),
  })
    .then((response: AJAXResponsePurchaseProduct) => {
      oncheckoutDownloadSuccess(response);
    })
    .catch(() => {
      showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
    });
}

function onCheckoutCloseCallback(): void {
  if (typeof stripeCheckoutCloseCallback === 'function') {
    stripeCheckoutCloseCallback();
  }
}

function oncheckoutDownloadSuccess(response: AJAXResponsePurchaseProduct): void {
  if (typeof onCheckoutDownloadSuccessCallback === 'function') {
    onCheckoutDownloadSuccessCallback();
  }

  if (response.url) {
    window.location.href = response.url;
  } else if (response.checkout) {
    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForDownloadPurchase(
        response.checkout.priceId,
        Number(response.checkout.id),
        response.checkout.posterHash,
        Number(response.checkout.idProduct),
        response.checkout.is ?? false,
        showDownloadProgress,
        onCheckoutCloseCallback
      );
    });
  } else if (response.premium > 0 && response.message.length > 0) {
    showErrorOnPanel(window.i18next.t('pmwjs_stock_cannot_be_downloaded'));
  }
}

function regeneratePremiumOrder(idOrder: string | null): void {
  void window.PMW.writeLocal('cart/premiumRegen/', {id: idOrder})
    .then(onGenerateOrderSuccess)
    .catch(() => {
      showErrorOnPanel(window.i18next.t('pmwjs_something_wrong_reload_page'));
    });
}

function regenerateNonPremiumOrder(idOrder: string | null): void {
  void window.PMW.writeLocal(`cart/nonPremiumRegen/${idOrder}`, {})
    .then(onGenerateOrderSuccess)
    .catch(() => {
      showErrorOnPanel(window.i18next.t('pmwjs_something_wrong_reload_page'));
    });
}

function regenerateOrder(idOrder: string | null): void {
  if (isPremiumUser(getUserPremiumLevel())) {
    regeneratePremiumOrder(idOrder);
  } else {
    regenerateNonPremiumOrder(idOrder);
  }
}

function openCheckoutDialogForTrialConversion(trialConversionMode: string, productId: number, pageHashedIds?: string[]): void {
  window.PMW.premiumDialogs.openConvertTrialDialog(
    trialConversionMode,
    onConvertTrial.bind(null, productId, pageHashedIds, undefined),
    onConvertTrialFailCallback.bind(trialConversionMode),
    onCheckoutCloseCallback
  );
}

function onConvertTrial(productId: number, pageHashedIds?: string[], data?: AJAXResponseOutOfFreeDownloads): void {
  window.PMW.trackOnlineAdsEvent(
    window.PMW.gtm.ONLINE_ADS_EVENT_TYPE_SUBSCRIPTION_PURCHASE,
    window.PMW.gtm.PURCHASE_ORDER_TYPE_SUBSCRIPTION,
    String(data?.orderValue),
    data?.idSubscriptionPurchase
  );
  trackGA4PurchaseOnConvertTrial(data);
  newPremiumDownload(productId, productUsed.hashedId, pageHashedIds);
}

export function onConvertTrialFailCallback(trialConversionReason: string): void {
  if (trialConversionReason === CONVERT_TRIAL_MODE_OUT_OF_FREE_DOWNLOADS) {
    void window.PMW.writeLocal('premium/cancelTrialSubscriptionOutOfFreeDownloads').catch(() => {
      showErrorOnPanel(window.i18next.t('pmwjs_something_wrong_reload_page'));
    });
  }
}

function trackGA4PurchaseOnConvertTrial(data?: AJAXResponseOutOfFreeDownloads): void {
  if (data?.ga4PurchaseEventParams) {
    window.PMW.gtm.trackGA4PurchaseEvent(data.ga4PurchaseEventParams);
  }
}

const onStripeCheckoutForDownloadPurchase = (response: AJAXResponsePurchaseProduct): void => {
  window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
    window.PMW.stripeCheckout.showStripeCheckoutForDownloadPurchase(
      response?.checkout?.priceId ?? 0,
      parseInt(response?.checkout?.id ?? '', 10),
      response?.checkout?.posterHash ?? '',
      parseInt(response?.checkout?.idProduct, 10),
      response.checkout.is,
      showDownloadProgress,
      onCheckoutCloseCallback
    );
  });
};

const onStripeCheckoutPreProcessing = (response: AJAXResponsePurchaseProduct): void => {
  let status: Status = Status.UNDEFINED;
  let idProduct = 0;
  if (response.status && response.status.length > 0) {
    status = response.status;
  }

  if (typeof response.premiumPurchase !== 'undefined') {
    idProduct = response.premiumPurchase.idProduct;
    if (idProduct > 0) {
      if (status === Status.NEW_ORDER) {
        const pageHashedId = window.PMW.redux.store.getState().publishingOptionsPanel.posterPageHashedId;
        executeOnPosterSave(() => {
          newPremiumDownload(idProduct, productUsed.posterId, pageHashedId ? [pageHashedId] : []);
        });
      } else if (status === Status.OUT_OF_FREE_DOWNLOADS) {
        window.PMW.gtm.trackGA4CustomEvent('TrialUserOutOfDownloads');
        // openCheckoutDialogForTrialConversion(status, String(idProduct), parseInt(response.premiumPurchase.creditPrice ?? '', 10));
        openCheckoutDialogForTrialConversion(status, idProduct);
      } else {
        showErrorOnPanel(window.i18next.t('pmwjs_checkout_download_error'));
      }
    } else {
      showErrorOnPanel(window.i18next.t('pmwjs_checkout_download_error'));
    }
  } else {
    showErrorOnPanel(window.i18next.t('pmwjs_checkout_download_error'));
  }
};

const onCheckoutForV2 = (response: AJAXResponsePurchaseProduct): void => {
  if (typeof response.checkout.doUpsellPremium !== 'undefined') {
    // Set the flag to show trial dialog on download purchase.
  }
  onStripeCheckoutForDownloadPurchase(response);
};

function callBackForCheckoutThroughReactBuilder(response: AJAXResponsePurchaseProduct): void {
  if (response.isPremiumUser) {
    onStripeCheckoutPreProcessing(response);
  } else if (response.checkout) {
    onCheckoutForV2(response);
  } else {
    showErrorOnPanel(window.i18next.t('pmwjs_checkout_download_error'));
  }
}

function executeOnPosterSave(callback: VoidFunction): void {
  void window.posterEditor?.whiteboard?.savePoster?.save().then(callback);
}

function processDesignForOutdatedOrder(orderId: string | null): void {
  if (orderId) {
    if (window.PMW.isReactPosterMaker()) {
      executeOnPosterSave(() => {
        regenerateOrder(orderId);
      });
    } else {
      regenerateOrder(orderId);
    }

    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.DOWNLOAD, {[GA4EventParamName.TYPE]: 'freeRegen'});
  } else {
    showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
  }
}

function processDesignForNewOrder(productId: number, idPosterHash: string, pageHashedIds: string[]): void {
  if (productId) {
    const downloadFn = isUserPAYG() ? newPurchaselessPaygDownload : newPremiumDownload;
    if (window.PMW.isReactPosterMaker()) {
      executeOnPosterSave(() => {
        downloadFn(productId, idPosterHash, pageHashedIds);
      });
    } else {
      downloadFn(productId, idPosterHash, pageHashedIds);
    }
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.DOWNLOAD, {[GA4EventParamName.TYPE]: 'freePremium'});
  } else {
    showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
  }
}

function processDesignForOutOfFreeDownloadsOrder(status: string, productId: number, pageHashedIds: string[]): void {
  window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.DOWNLOAD, {[GA4EventParamName.TYPE]: 'TrialUserOutOfDownloads'});
  if (productId) {
    openCheckoutDialogForTrialConversion(status, productId, pageHashedIds);
  } else {
    showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
  }
}

function processDesignForDownloadableOrder(orderId: string | null): void {
  if (orderId) {
    void updateOrderLastDownloadedOn(orderId);
  }
  window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.DOWNLOAD, {[GA4EventParamName.TYPE]: 'redownload'});
  window.PMW.redux.store.dispatch(setShowLoader(false));
}

function processDesignForGeneratingOrder(orderId: string | null): void {
  const object: AJAXResponseOrderSuccess = {
    type: '',
    isRealTime: false,
    idOrder: orderId,
  };
  showDownloadProgress(object);
}

function processDesignForFreebieOrder(idPosterHash: string): void {
  if (window.PMW.isReactPosterMaker()) {
    onFreeDownloadFromReactBuilder(idPosterHash);
  } else if (productUsed.isVideo) {
    downloadVideoFreebie(idPosterHash);
  } else {
    downloadImagefreebie(idPosterHash);
  }
  window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.DOWNLOAD, {[GA4EventParamName.TYPE]: 'free'});
}

const onUnpurchasedOrderProcessing = (productId: number): void => {
  const poster = window.posterEditor ? window.posterEditor.whiteboard : undefined;
  if (Number(productId) > 0 && poster) {
    checkoutDownload({
      idposter: poster.hashedID,
      idproduct: productId,
      promotion: '',
      mode: PublishAndDownloadMode.DOWNLOAD,
      phidsJson: JSON.stringify([window.PMW.redux.store.getState().publishingOptionsPanel.posterPageHashedId]),
    })
      .then((response: AJAXResponsePurchaseProduct) => {
        onCheckoutDownloadResponse(response);
      })
      .catch(() => {
        showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
      });
  }
};

function processDesignForUnpurchasedOrder(productId: number, idPosterHash: string): void {
  if (productId) {
    if (window.PMW.isReactPosterMaker()) {
      executeOnPosterSave(() => {
        onUnpurchasedOrderProcessing(productId);
      });
    } else if (Number(window.PMW.getUserId()) > 0) {
      purchaseDownload(productId, idPosterHash);
    } else {
      window.location.href = window.PMW.util.site_url('authenticate/showlogin');
    }
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.DOWNLOAD, {[GA4EventParamName.TYPE]: 'currency'});
  } else {
    showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
  }
}

function processDesignForNeedsAttentionStatus(): void {
  showErrorOnPanel(window.i18next.t('pmwjs_something_wrong_contact_support', {supportLink: window.PMW.util.site_url('user/feedbackform')}));
}

function onCheckoutDownloadResponse(response: AJAXResponsePurchaseProduct): void {
  if (response === null) {
    showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
  } else {
    if (response.orderRegen) {
      window.PMW.freeEditDialog(response);
      return;
    }
    if (response.message && response.premium > 0) {
      showErrorOnPanel(window.i18next.t('pmwjs_cannot_purchase_premium_content'));
    } else if (response.url) {
      window.location.href = response.url;
    } else if (response.isPremiumUser || response.checkout) {
      callBackForCheckoutThroughReactBuilder(response);
    } else {
      showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
    }
  }
}

function checkoutDownload(p: CheckoutDownloadProps): Promise<AJAXResponsePurchaseProduct> {
  return window.PMW.writeLocal('cart/checkoutPublish', p);
}

const updateOrderLastDownloadedOn = async (orderId: string): Promise<void> => {
  await window.PMW.writeLocal('cart/updateOrderLastDownloadedOn', {
    orderId,
  });
};
